import React, { Suspense, useEffect } from "react"
import ReactDOM from "react-dom/client"
import { Provider } from "react-redux"
import { BrowserRouter } from "react-router-dom"

import { store } from "./core/app/store"
import { loadAWSRUMClient, getAWSRUMClient, recordConsoleError } from "./core/global/awsRumClient"
import oktaAuthClient from "./core/config/okta.config"
import { resourcesSynchronization } from "./gluelayer"
import { clearOtherCaches } from "./core/app/slices/cache/cacheSlice"

const App = React.lazy(() => import("./App"))

const RootApp = () => {
  // Defer loading heavy global styles (includes fonts) until after mount
  useEffect(() => {
    import("./index.scss")
  }, [])

  return (
    <Suspense fallback={null}>
      <App />
    </Suspense>
  )
}

// filter the ignorable errors
const originalConsoleError = console.error;
const errFilterList = [
  "Support for defaultProps will be removed",
]

oktaAuthClient.authStateManager.subscribe((authState) => {
  loadAWSRUMClient(oktaAuthClient.getAccessToken())
})

resourcesSynchronization.setZipGuardReporter((eventName, payload) => {
  getAWSRUMClient()?.recordEvent(eventName, payload)
})

// the virtual FS holds one case at a time: zips cached for the others would
// stay in memory until the tab is closed
resourcesSynchronization.setCaseClaimedListener((caseId) => {
  store.dispatch(clearOtherCaches({ caseId }))
})

window.onerror = function (message, source, lineno, colno, error) {
  if (message && typeof message=== 'string' && message.includes(`unwind`)) {
    return
  }
  getAWSRUMClient()?.recordError(error || message);
};

window.onunhandledrejection = function (event) {
  getAWSRUMClient()?.recordError(event.reason);
};


console.error = (...args) => {
  try {
    // Used only for the local filter below; never sent to RUM.
    const errorMessage = args.map(arg => {
      if (arg instanceof Error) return arg.stack || arg.message;
      if (typeof arg === 'object' && arg !== null) return '[object]';
      return String(arg);
    }).join(' ');

    if (errorMessage  && errorMessage.includes(`unwind`)) {
      return
    }

    recordConsoleError(args);
    if (args.length > 0 && typeof args[0] === "string") {
      const shouldSuppress = errFilterList.some((filter) => args[0].includes(filter));
      if (!shouldSuppress) {
        originalConsoleError(...args);
      }
    } else {
      originalConsoleError(...args);
    }
  } catch (error) {
    // Error in custom console.error override
    originalConsoleError(...args);
  }
};

const contentElement = document.getElementById("root") as HTMLElement

ReactDOM.createRoot(contentElement).render(
  <Provider store={store}>
    <BrowserRouter>
      <RootApp />
    </BrowserRouter>
  </Provider>,
)